import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchPost } from '../../store/fetch';
import { IStoreState } from '../../store/IStoreState';
import { AssetTypes } from '../../utils/enums/AssetTypes';
import {
    Content,
    EditPanel,
    EditPanelButtons,
    EditPanelCell,
    EditPanelRow,
    EditPanelSection,
    Panel,
    PanelBody,
    PanelRouterLink,
    Topbar,
    TopbarCrumb,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../elements';
import { Spinner } from '../spinners';
import { AssetDropzone } from '../Surveys/Common/AssetDropzone';

interface IImportSurveyProps {
    match: {
        params: {
            id?: string;
        };
    };
}
export function ImportSurvey(props: IImportSurveyProps) {
    const objectId = props.match.params.id;
    const translate = useTranslate();
    const [uploadedAsset, setUploadedAsset] = React.useState<{ assetId: string } | undefined>();
    const [result, setResult] = React.useState<{ isSuccessful: boolean; messages: string[]; surveyId?: string } | undefined>();
    const useProductTitle = useSelector((state: IStoreState) => state.authentication.useProductTitle);

    const onAssetUploaded = async (assetId: string) => {
        setUploadedAsset({ assetId });

        try {
            const response = await fetchPost(`/api/objects/${objectId}/importsurvey`, undefined, { assetId });
            setResult(response.json);
        } catch (error) {
            setResult({ isSuccessful: false, messages: [error.message] });
        }
    };

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive
                        title={
                            useProductTitle
                                ? translate('common.importItem', translate('common.product'))
                                : translate('common.importItem', translate('common.survey'))
                        }
                        icon="fa-upload"
                    />
                    <TopbarCrumb>
                        <Link to={`/objects/${objectId}`}>{translate('common.objectDetails')}</Link>
                    </TopbarCrumb>
                    <TopbarCrumb>
                        <Link to="/objects">{translate('common.objects')}</Link>
                    </TopbarCrumb>
                </TopbarLeft>
                <TopbarRight>
                    <PanelRouterLink to={`/objects/${objectId}`} text={translate('object.showObject')} small icon="fa fa-search" />
                </TopbarRight>
            </Topbar>
            <Content>
                <Tray>
                    <Panel>
                        <PanelBody>
                            <EditPanel>
                                <EditPanelRow>
                                    {uploadedAsset === undefined && (
                                        <EditPanelSection>
                                            <AssetDropzone
                                                acceptedFiles=".tar"
                                                onComplete={(file) => {
                                                    if (file.status === 'success') {
                                                        void onAssetUploaded(file.s3Id);
                                                    }
                                                }}
                                                objectId={objectId}
                                                assetType={AssetTypes.Import}
                                                autoProcessQueue={true}
                                                maxFiles={1}
                                            />
                                        </EditPanelSection>
                                    )}
                                    {uploadedAsset !== undefined && result === undefined && (
                                        <EditPanelCell rowCount={6}>
                                            Import uploaded, processing <Spinner />
                                        </EditPanelCell>
                                    )}
                                    {result && (
                                        <EditPanelCell rowCount={6}>
                                            {result.isSuccessful ? 'Import was successful' : 'Import failed'} <br />
                                            Report: <br />
                                            <ul>
                                                {result.messages.map((message, index) => (
                                                    <li key={index}>{message}</li>
                                                ))}
                                            </ul>
                                        </EditPanelCell>
                                    )}
                                </EditPanelRow>
                            </EditPanel>
                        </PanelBody>
                        {result && (
                            <EditPanelButtons footer>
                                <PanelRouterLink to={`/surveys/${result.surveyId}`} text="Go to imported survey" small primary />
                            </EditPanelButtons>
                        )}
                    </Panel>
                </Tray>
            </Content>
        </>
    );
}
